import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { DeclarationBox } from './DeclarationBox';
import { type DeclarationValues, DeclarationType } from './types';
import { FundingSource, type FundingsValue } from '../FundingModule';
import {
  NominationDeclaration,
  useUpdateNomination,
} from '../../../graphql/NominationForm';
import { Alert, type AlertDetails } from '../../../../../components';

type DeclarationProps = {
  isViewMode?: boolean;
  fundings: FundingsValue[];
  declarations: DeclarationValues[];
  refetchDeclarations: () => void;
};

export const Declaration: React.FC<DeclarationProps> = props => {
  const { nominationId } = useParams();
  const {
    refetchDeclarations,
    fundings = [],
    declarations,
    isViewMode = false,
  } = props;
  let declarationType = DeclarationType.ALL;
  const initialValues: DeclarationValues[] = [
    {
      content: NominationDeclaration.NO_INTEREST_CONFLICT,
      confirmed: false,
      justification: '',
    },
    {
      content: NominationDeclaration.NOT_REGISTER_HIGHER_DEGREE,
      confirmed: false,
    },
    {
      content: NominationDeclaration.INFORMATION_TRUE,
      confirmed: false,
    },
  ];

  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });

  const fetchDeclaration = (): DeclarationValues[] => {
    if (declarations.length) return declarations;

    const ifHKPFS = fundings?.some(
      funding =>
        funding?.source === FundingSource.HONGKONG_PHD_FELLOWSHIP_SCHEME,
    );
    const ifExternalorDEA = fundings?.some(
      funding =>
        funding?.source === FundingSource.EXTERNAL_GRANTS_FUNDS ||
        funding?.source === FundingSource.INTERNAL_GRANTS_FUNDS ||
        funding?.source === FundingSource.DEPARTMENTAL_EARNINGS_ACCOUNT,
    );

    declarationType =
      ifExternalorDEA && ifHKPFS
        ? DeclarationType.BOTH
        : ifExternalorDEA
          ? DeclarationType.EXTERNAL
          : ifHKPFS
            ? DeclarationType.HKPFS
            : DeclarationType.ALL;

    return updateInitialValues(declarationType);
  };

  function updateInitialValues(Type: DeclarationType): DeclarationValues[] {
    const newInitialValues: DeclarationValues[] = [...initialValues];

    switch (Type) {
      case DeclarationType.ALL:
        break;
      case DeclarationType.HKPFS:
        if (newInitialValues.length < 4)
          newInitialValues.push({
            content: NominationDeclaration.APPLICANT_MEET_HKPFS_THRESHOLD,
            confirmed: false,
          });
        break;
      case DeclarationType.EXTERNAL:
        if (newInitialValues.length < 4)
          newInitialValues.push({
            content: NominationDeclaration.ADMISSION_USING_EXTERNAL_OR_DEA,
            confirmed: false,
          });
        break;
      case DeclarationType.BOTH:
        if (newInitialValues.length < 4)
          newInitialValues.push(
            {
              content: NominationDeclaration.APPLICANT_MEET_HKPFS_THRESHOLD,
              confirmed: false,
            } as DeclarationValues,
            {
              content: NominationDeclaration.ADMISSION_USING_EXTERNAL_OR_DEA,
              confirmed: false,
            } as DeclarationValues,
          );
        break;
      default:
    }

    return newInitialValues;
  }

  const updateNomination = useUpdateNomination(refetchDeclarations);
  const handleSubmit = async (value: DeclarationValues[]): Promise<void> => {
    const variables = {
      id: nominationId,
      input: {
        declarations: value,
      },
    };
    try {
      await updateNomination({
        variables,
      });
      setAlertDetails({
        alertContent: 'Saved Declaration Successfully.',
        alertType: 'success',
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error: any) {
      setAlertDetails({
        alertContent: 'Saved Declaration Failed.' + (error as Error)?.message,
        alertType: 'error',
      });
    }
    refetchDeclarations();
    setOpenAlert(true);
  };

  return (
    <div>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <DeclarationBox
        isViewMode={isViewMode}
        initialValues={fetchDeclaration()}
        onSubmit={handleSubmit}
        declarationType={declarationType}
      />
    </div>
  );
};
